import Vue from 'vue';
import VueRouter from 'vue-router';
import Store from '@/store/index';
import TenantService from '@/services/TenantService';
import UserService from '@/services/UserService';
import ApiService from '@/services/ApiService';
import Terminal from '@/models/Terminal';

import TenantLogin from '@/views/shared/TenantLogin';
import UserLogin from '@/views/shared/UserLogin';
import DebugConsole from '@/views/shared/DebugConsole';

import TablesLayout from '@/views/aria/tables/TablesLayout';
import Tables from '@/views/aria/tables/Tables';
import OrderLayout from '@/views/aria/order/OrderLayout';
import Order from '@/views/aria/order/Order';

import AllegraOrderLayout from '@/views/allegra/order/OrderLayout';
import AllegraOrder from '@/views/allegra/order/Order';

import KitchenDisplayLayout from '@/views/aria/kitchen/KitchenDisplayLayout.vue';
import KitchenDisplay from '@/views/aria/kitchen/KitchenDisplay.vue';

const MobileTest = () => import('@/views/shared/MobileTest');
const PrintTest = () => import('@/views/shared/PrintTest');
// const ViewInvoice = () => import('@/views/shared/ViewInvoice');
const InvoiceScan = () => import('@/views/shared/InvoiceScan');
const SelectLocale = () => import('@/views/shared/SelectLocale');
const TenantRegister = () => import('@/views/shared/TenantRegister');
const GettingStarted = () => import('@/views/shared/gettingStarted/GettingStarted');

const AdminLayout = () => import('@/views/shared/admin/AdminLayout');
const Dashboard = () => import('@/views/shared/admin/dashboard/Dashboard');
const Clients = () => import('@/views/shared/admin/clients/Clients');
const ClientTransactionsSummary = () => import('@/views/shared/admin/clients/ClientTransactionsSummary');
const ClientTransactions = () => import('@/views/shared/admin/clients/ClientTransactions');
const BankStatements = () => import('@/views/shared/admin/bankStatements/BankStatements');
const BankStatement = () => import('@/views/shared/admin/bankStatements/BankStatement');
const OrderEdit = () => import('@/views/shared/admin/orders/OrderEdit');
const Account = () => import('@/views/shared/admin/account/Account');
const Billing = () => import('@/views/shared/admin/Billing');
const Dispatches = () => import('@/views/shared/admin/Dispatches');
const OrderItemComments = () => import('@/views/shared/admin/OrderItemComments');
const Products = () => import('@/views/shared/admin/Products');
const ProductsPriceTags = () => import('@/views/shared/admin/ProductsPriceTags');
const Pricelists = () => import('@/views/shared/admin/Pricelists');
const Orders = () => import('@/views/shared/admin/orders/Orders');
const TaxRates = () => import('@/views/shared/admin/TaxRates');
const Printers = () => import('@/views/shared/admin/Printers');
const Receptions = () => import('@/views/shared/admin/Receptions');
const Reports = () => import('@/views/shared/admin/Reports');
const TablesSettings = () => import('@/views/shared/admin/TablesSettings');
const OrderSettings = () => import('@/views/shared/admin/OrderSettings');
const Terminals = () => import('@/views/shared/admin/terminals/Terminals');
const BeginningInventories = () => import('@/views/shared/admin/beginningInventories/BeginningInventories');
const BeginningInventory = () => import('@/views/shared/admin/beginningInventories/BeginningInventory');
const PriceCalculations = () => import('@/views/shared/admin/priceCalculations/PriceCalculations');
const PriceCalculation = () => import('@/views/shared/admin/priceCalculations/PriceCalculation');
const PriceLevelings = () => import('@/views/shared/admin/priceLevelings/PriceLevelings');
const PriceLeveling = () => import('@/views/shared/admin/priceLevelings/PriceLeveling');
const WriteOffs = () => import('@/views/shared/admin/writeOffs/WriteOffs');
const WriteOff = () => import('@/views/shared/admin/writeOffs/WriteOff');
const Stock = () => import('@/views/shared/admin/stock/Stock');
const StockItem = () => import('@/views/shared/admin/stock/StockItem');
const SalesPurchaseLedger = () => import('@/views/shared/admin/salesPurchaseLedger/SalesPurchaseLedger');
const CashSales = () => import('@/views/shared/admin/cashSales/CashSales');
const UserRoles = () => import('@/views/shared/admin/UserRoles');
const Users = () => import('@/views/shared/admin/Users');
const WorkingHours = () => import('@/views/shared/admin/WorkingHours');
const Categories = () => import('@/views/shared/admin/Categories');

const Barcodes = () => import('@/views/allegra/admin/Barcodes');
const Shortcuts = () => import('@/views/allegra/admin/Shortcuts');

import EinvoiceService from '@/services/EinvoiceService';

const router = new VueRouter({
    routes: [
        {
            path: '/',
            redirect: '/login',
        },
        {
            path: '/test',
            name: 'test',
            component: MobileTest,
        },
        {
            path: '/print-test',
            name: 'printTest',
            component: PrintTest,
        },
        // {
        //     path: '/invoice/:hash',
        //     name: 'viewInvoice',
        //     component: ViewInvoice
        // },
        {
            path: '/invoice-scan/:token',
            name: 'invoiceScan',
            component: InvoiceScan,
        },
        {
            path: '/select-locale',
            name: 'selectLocale',
            component: SelectLocale,
        },
        {
            path: '/login',
            name: 'tenantLogin',
            component: TenantLogin,
        },
        {
            path: '/register/:subscription?',
            name: 'tenantRegister',
            component: TenantRegister,
        },
        {
            path: '/getting-started',
            name: 'gettingStarted',
            component: GettingStarted,
        },
        {
            path: '/user-login',
            name: 'userLogin',
            component: UserLogin,
        },
        {
            path: '/debug-console',
            name: 'debugConsole',
            component: DebugConsole,
        },
        {
            path: '/tables',
            name: 'tablesLayout',
            redirect: '/tables/:show?',
            component: TablesLayout,
            meta: { requiredPermission: 'nav_access_tables' },
            children: [
                {
                    path: ':show?',
                    name: 'tables',
                    component: Tables,
                },
            ],
        },
        {
            path: '/order',
            name: 'orderLayout',
            redirect: '/order/:table_id?',
            component: OrderLayout,
            meta: { requiredPermission: 'nav_access_order' },
            children: [
                {
                    path: ':table_id?',
                    name: 'order',
                    component: Order,
                },
            ],
        },
        {
            path: '/kitchen',
            name: 'kitchen',
            redirect: '/kitchen/open',
            component: KitchenDisplayLayout,
            meta: { enabledWithModules: ['kds'] },
            children: [
                {
                    path: 'open',
                    name: 'kitchenOpenTickets',
                    component: KitchenDisplay,
                },
                {
                    path: 'done',
                    name: 'kitchenDoneTickets',
                    component: KitchenDisplay,
                },
            ],
        },
        {
            path: '/allegra/order',
            name: 'allegraOrderLayout',
            redirect: '/allegra/order/:show?',
            component: AllegraOrderLayout,
            children: [
                {
                    path: ':show?',
                    name: 'allegraOrder',
                    component: AllegraOrder,
                },
            ],
        },
        {
            path: '/admin',
            name: 'admin',
            redirect: '/admin/dashboard',
            component: AdminLayout,
            meta: { requiredPermission: 'nav_access_admin' },
            children: [
                {
                    path: 'dashboard',
                    name: 'adminDashboard',
                    component: Dashboard,
                    meta: { requiredPermission: 'access_dashboard' },
                },
                {
                    path: 'users',
                    name: 'adminUsers',
                    component: Users,
                    meta: { requiredPermission: 'access_users' },
                },
                {
                    path: 'user-groups',
                    name: 'adminUserRoles',
                    component: UserRoles,
                    meta: { requiredPermission: 'access_user_roles' },
                },
                {
                    path: 'inventory',
                    name: 'adminProducts',
                    component: Products,
                    meta: { requiredPermission: 'access_products' },
                },
                {
                    path: 'inventory/price-tags',
                    name: 'adminProductsPriceTags',
                    component: ProductsPriceTags,
                    meta: { requiredPermission: 'access_products' },
                },
                {
                    path: 'pricelists',
                    name: 'adminPricelists',
                    component: Pricelists,
                    meta: { requiredPermission: 'access_products' },
                },
                {
                    path: 'order-item-comments',
                    name: 'adminOrderItemComments',
                    component: OrderItemComments,
                    meta: { requiredPermission: 'access_order_item_comments' },
                },
                {
                    path: 'categories',
                    name: 'adminProductCategories',
                    component: Categories,
                    meta: { requiredPermission: 'access_product_categories' },
                },
                {
                    path: 'delivery-in-notes',
                    name: 'adminReceptions',
                    component: Receptions,
                    meta: { requiredPermission: 'access_receptions' },
                },
                {
                    path: 'delivery-out-notes',
                    name: 'adminDispatches',
                    component: Dispatches,
                    meta: { requiredPermission: 'access_dispatches' },
                },
                {
                    path: 'beginning-inventories',
                    name: 'adminBeginningInventories',
                    component: BeginningInventories,
                    meta: { enabledWithModules: ['inventoryAccounting'] },
                    // meta: { requiredPermission: 'access_beginning_inventories' },
                },
                {
                    path: 'beginning-inventories/new',
                    name: 'adminBeginningInventoryCreate',
                    component: BeginningInventory,
                    meta: { enabledWithModules: ['inventoryAccounting'] },
                    // meta: { requiredPermission: 'access_beginning_inventories' },
                },
                {
                    path: 'beginning-inventories/:id?',
                    name: 'adminBeginningInventoryUpdate',
                    component: BeginningInventory,
                    meta: { enabledWithModules: ['inventoryAccounting'] },
                    // meta: { requiredPermission: 'access_beginning_inventories' },
                },
                {
                    path: 'price-calculations',
                    name: 'adminPriceCalculations',
                    component: PriceCalculations,
                    meta: { enabledWithModules: ['inventoryAccounting'] },
                    // meta: { requiredPermission: 'access_price_calculations' },
                },
                {
                    path: 'price-calculations/new',
                    name: 'adminPriceCalculationCreate',
                    component: PriceCalculation,
                    meta: { enabledWithModules: ['inventoryAccounting'] },
                    // meta: { requiredPermission: 'access_price_calculations' },
                },
                {
                    path: 'price-calculations/:id?',
                    name: 'adminPriceCalculationUpdate',
                    component: PriceCalculation,
                    meta: { enabledWithModules: ['inventoryAccounting'] },
                    // meta: { requiredPermission: 'access_price_calculations' },
                },
                {
                    path: 'price-levelings',
                    name: 'adminPriceLevelings',
                    component: PriceLevelings,
                    meta: { enabledWithModules: ['inventoryAccounting'] },
                    // meta: { requiredPermission: 'access_price_levelings' },
                },
                {
                    path: 'price-levelings/new',
                    name: 'adminPriceLevelingCreate',
                    component: PriceLeveling,
                    meta: { enabledWithModules: ['inventoryAccounting'] },
                    // meta: { requiredPermission: 'access_price_levelings' },
                },
                {
                    path: 'price-levelings/:id?',
                    name: 'adminPriceLevelingUpdate',
                    component: PriceLeveling,
                    meta: { enabledWithModules: ['inventoryAccounting'] },
                    // meta: { requiredPermission: 'access_price_levelings' },
                },
                {
                    path: 'write-offs',
                    name: 'adminWriteOffs',
                    component: WriteOffs,
                    meta: { enabledWithModules: ['inventoryAccounting'] },
                    // meta: { requiredPermission: 'access_write_offs' },
                },
                {
                    path: 'write-offs/new',
                    name: 'adminWriteOffCreate',
                    component: WriteOff,
                    meta: { enabledWithModules: ['inventoryAccounting'] },
                    // meta: { requiredPermission: 'access_write_offs' },
                },
                {
                    path: 'write-offs/:id?',
                    name: 'adminWriteOffUpdate',
                    component: WriteOff,
                    meta: { enabledWithModules: ['inventoryAccounting'] },
                    // meta: { requiredPermission: 'access_write_offs' },
                },
                {
                    path: 'stock',
                    name: 'adminStock',
                    component: Stock,
                    meta: { enabledWithModules: ['inventoryAccounting'] },
                    // meta: { requiredPermission: 'access_stock' },
                },
                {
                    path: 'stock-item/:id?',
                    name: 'adminStockItem',
                    component: StockItem,
                    meta: { enabledWithModules: ['inventoryAccounting'] },
                    // meta: { requiredPermission: 'access_stock_item' },
                },
                {
                    path: 'kep',
                    name: 'adminSalesPurchaseLedger',
                    component: SalesPurchaseLedger,
                    meta: { enabledWithModules: ['inventoryAccounting'] },
                    // meta: { requiredPermission: 'access_sales_purchase_ledger' },
                },
                {
                    path: 'cash-sales',
                    name: 'adminCashSales',
                    component: CashSales,
                    meta: { enabledWithModules: ['inventoryAccounting'] },
                    // meta: { requiredPermission: 'access_cash_sales' },
                },
                {
                    path: 'orders',
                    name: 'adminOrders',
                    component: Orders,
                    meta: { requiredPermission: 'access_orders' },
                },
                {
                    path: 'reports',
                    name: 'adminReports',
                    component: Reports,
                    meta: { requiredPermission: 'access_reports' },
                },
                {
                    path: 'working-hours',
                    name: 'adminWorkingHours',
                    component: WorkingHours,
                    meta: { requiredPermission: 'access_working_hours' },
                },
                {
                    path: 'tables-settings',
                    name: 'adminTablesSettings',
                    component: TablesSettings,
                    meta: { requiredPermission: 'access_tables_settings' },
                },
                {
                    path: 'order-settings',
                    name: 'adminOrderSettings',
                    component: OrderSettings,
                    meta: { requiredPermission: 'access_order_settings' },
                },
                {
                    path: 'terminals',
                    name: 'adminTerminals',
                    component: Terminals,
                    meta: { requiredPermission: 'access_terminals' },
                },
                {
                    path: 'tax-rates',
                    name: 'adminTaxRates',
                    component: TaxRates,
                    meta: { requiredPermission: 'access_tax_rates' },
                },
                {
                    path: 'printers',
                    name: 'adminPrinters',
                    component: Printers,
                    meta: { requiredPermission: 'access_printers' },
                },
                {
                    path: 'clients',
                    name: 'adminClients',
                    component: Clients,
                    meta: { requiredPermission: 'access_clients' },
                },
                {
                    path: 'client-transactions/summary',
                    name: 'adminClientTransactionsSummary',
                    component: ClientTransactionsSummary,
                    meta: {
                        requiredPermission: 'access_client_transactions',
                        enabledWithModules: ['inventoryAccounting', 'einvoices'],
                    },
                },
                {
                    path: 'client-transactions/:client_id?',
                    name: 'adminClientTransactions',
                    component: ClientTransactions,
                    meta: {
                        requiredPermission: 'access_client_transactions',
                        enabledWithModules: ['inventoryAccounting', 'einvoices'],
                    },
                },
                {
                    path: 'bank-statements',
                    name: 'adminBankStatements',
                    component: BankStatements,
                    meta: {
                        requiredPermission: 'access_bank_statements',
                        enabledWithModules: ['inventoryAccounting', 'einvoices'],
                    },
                },
                {
                    path: 'bank-statements/new',
                    name: 'adminBankStatementCreate',
                    component: BankStatement,
                    meta: {
                        requiredPermission: 'access_bank_statements',
                        enabledWithModules: ['inventoryAccounting', 'einvoices'],
                    },
                },
                {
                    path: 'bank-statements/:id?',
                    name: 'adminBankStatementUpdate',
                    component: BankStatement,
                    meta: {
                        requiredPermission: 'access_bank_statements',
                        enabledWithModules: ['inventoryAccounting', 'einvoices'],
                    },
                },
                {
                    path: 'order/new',
                    name: 'adminOrderCreate',
                    component: OrderEdit,
                    meta: { requiredPermission: 'access_orders' }
                },
                {
                    path: 'order/:id?',
                    name: 'adminOrderUpdate',
                    component: OrderEdit,
                    meta: { requiredPermission: 'access_orders' }
                },
                {
                    path: 'barcodes',
                    name: 'adminBarcodes',
                    component: Barcodes,
                    meta: { requiredPermission: 'access_barcodes' },
                },
                {
                    path: 'shortcuts',
                    name: 'adminShortcuts',
                    component: Shortcuts,
                    meta: { requiredPermission: 'access_shortcuts' },
                },
                {
                    path: 'account',
                    name: 'adminAccount',
                    component: Account,
                    meta: { requiredPermission: 'access_account' },
                },
                {
                    path: 'billing',
                    name: 'adminBilling',
                    component: Billing,
                    meta: { requiredPermission: 'access_account' },
                },
            ],
        },
    ],
});

router.beforeEach(async (to, from, next) => {
    if (to.name === 'selectLocale') {
        return next();
    }

    let tenant = Store.getters.tenant;
    let user = Store.getters.user;

    if (!tenant) {
        const params = {};
        if (to.query.tntoken) {
            params.tntoken = to.query.tntoken;
        }

        if (to.query.token) {
            params.token = to.query.token;
        }

        const redirectTo = await Store.dispatch('initializeApp', params);
        if (redirectTo) {
            return next({ name: redirectTo });
        }

        tenant = Store.getters.tenant;
        user = Store.getters.user;
    }

    if (
        !tenant
        && to.name !== 'tenantLogin'
        && to.name !== 'tenantRegister'
        && to.name !== 'viewInvoice'
        && to.name !== 'invoiceScan'
    ) {
        return next({ name: 'tenantLogin' });
    }

    if (tenant && to.name !== 'gettingStarted' && tenant.getstarted_status !== 'completed') {
        return next({ name: 'gettingStarted' });
    }

    if (
        tenant &&
        (
            to.name === 'tenantLogin' ||
            to.name === 'tenantRegister' ||
            (to.name === 'gettingStarted' && tenant.getstarted_status === 'completed')
        )
    ) {
        if (!user) {
            return next({ name: 'userLogin' });
        }

        return next(UserService.defaultRedirect() || false);
    }

    const enableSales = AppStorage.local().getItem('enable-web-sales') === 'true'
    const isWeb = Store.getters.device && Store.getters.device.platform === 'web'
    const isImpersonate = Store.getters.impersonate
    const isProduction = process.env.VUE_APP_ENV === 'production';
    const singleCashRegister = tenant && tenant.venue && !!tenant.venue.settings.cash_register.single

    if(!enableSales && !singleCashRegister && !isImpersonate && isWeb && isProduction && ['tables', 'order', 'allegraOrder'].includes(to.name)) {
        return next({ name: 'userLogin' });
    }

    if (to.name === 'allegraOrder' && !isAllegra) {
        return next({ name: 'tables' });
    }

    const terminal = await Terminal.current();
    const isKDSTerminal = terminal?.isKitchenDisplaySystem();
    const isKitchenRoute = to.name === 'kitchen' || to.name === 'kitchenOpenTickets' || to.name === 'kitchenDoneTickets'

    if(tenant && isKDSTerminal && !isKitchenRoute) {
        return next({ name: 'kitchen', query: to.query });
    }

    if (isKitchenRoute) {
        const isDonePageOnly = !isKDSTerminal && !UserService.allows('access_terminals');

        if(!isKDSTerminal && !user) {
            return next({ name: 'userLogin' });
        }

        if(isDonePageOnly && to.name !== 'kitchenDoneTickets') {
            return next({ name: 'kitchenDoneTickets', query: to.query });
        }
    }

    if ((to.name === 'tables' || to.name === 'order') && isAllegra) {
        return next({ name: 'allegraOrder' });
    }

    let requiredPermissions = to.matched.map(route => route.meta.requiredPermission).filter(p => !!p);
    if (!UserService.allows(requiredPermissions)) {
        return next({ name: 'userLogin' });
    }

    let enabledWithModules = to.matched
        .map(route => route.meta.enabledWithModules)
        .filter(p => !!p)
        .flat();

    if (enabledWithModules.length) {
        const modulesEnabled = enabledWithModules.map((mod) => {
            if (mod === 'inventoryAccounting') return !!tenant.venue?.settings?.use_inventory_accounting;
            if (mod === 'einvoices') return EinvoiceService.isEnabled();
            return Store.getters.modules[mod];
        });

        if (!modulesEnabled.some((enabled) => enabled == true)) {
            return next(false);
        }
    }

    return next();
});

export default router;
